/**
 * The picture a workspace has in the library: its newest generated picture, cropped to the
 * library's square, rather than a picture of the canvas. It is drawn from a copy on this
 * device when there is one (a file of the local folder, bytes this session holds, a picture
 * held in the record), so nothing is downloaded for it; without one, from the picture's cloud
 * address, which the browser has just shown and holds in its cache (asked for with CORS, so
 * the drawing can be read back). A workspace with no generated picture, or whose picture
 * cannot be had in time, gets a picture of its canvas as before (thumbnailGenerator.ts).
 */
import { NodeType, type CanvasNode } from '../types';
import { THUMBNAIL_SIZE, canDrawFrom } from './thumbnailGenerator';
import { findLocalDisplayUrl, peekDisplayUrl, resolveDisplayUrl, type DisplayOptions } from './displayUrl';
import { withinTime } from './pendingSaves';

const COVER_QUALITY = 0.78;
/** The picture is stored inside the workspace's own document, as the canvas picture is */
const MAX_COVER_CHARS = 200_000;
/** How long leaving a workspace spends on its cover at most, before the canvas is pictured instead */
export const COVER_WAIT_MS = 600;
/** The same, on a device without a local folder: the picture comes from the cloud (the browser's cache, mostly) */
export const COVER_WAIT_CLOUD_MS = 2500;
/** How many pictures are tried, newest first */
const MOST_TRIED = 3;
/** Where a tall picture is cut, from its top: people's faces are mostly in its upper part */
const TALL_CROP_FROM_TOP = 0.25;

/** The workspace's generated pictures, newest first: what each Image node shows, then storyboard frames */
export const coverCandidates = (nodes: ReadonlyArray<CanvasNode>): string[] => {
    const shown = nodes
        .filter(node => node.type === NodeType.IMAGE_GENERATOR)
        .map(node => {
            const all = Array.isArray(node.data?.generatedImages) ? node.data.generatedImages : [];
            return { url: node.data?.generatedImageUri || all[all.length - 1], at: node.data?.timestamp ?? 0 };
        })
        .filter((found): found is { url: string; at: number } => typeof found.url === 'string' && found.url.length > 0)
        .sort((a, b) => b.at - a.at)
        .map(found => found.url);
    const frames = nodes
        .filter(node => node.type === NodeType.STORYBOARD_NODE)
        .flatMap(node => (node.data?.storyboardConfig?.boxes ?? []).map(box => box.imageUrl))
        .filter((url): url is string => typeof url === 'string' && url.length > 0);
    return [...new Set([...shown, ...frames])];
};

/** A picture's address on another site (the cloud): drawn from with CORS, the one way a canvas drawn from it can be read back */
export const isCloudPictureUrl = (url: string): boolean => /^https?:\/\//i.test(url);

/**
 * An address the picture can be drawn from and read back: on this device, or held in the
 * record; else its cloud address, once the local folder has been looked in; else null
 */
export const drawableAddress = async (ref: string, options: DisplayOptions, pageOrigin: string): Promise<string | null> => {
    if (/^(data|blob):/i.test(ref)) return ref;
    const known = peekDisplayUrl(ref, options);
    if (known?.url && canDrawFrom(known.url, pageOrigin)) return known.url;
    const local = await findLocalDisplayUrl(ref, options);
    if (local?.url && canDrawFrom(local.url, pageOrigin)) return local.url;
    // Its cloud address: the one shown, or looked up now (from what this session read, mostly)
    const cloud = known?.url ? known : await resolveDisplayUrl(ref, options);
    return cloud?.url && isCloudPictureUrl(cloud.url) ? cloud.url : null;
};

/** Where in a picture the square is cut: the middle of a wide one, the upper part of a tall one */
export const coverCrop = (width: number, height: number) => {
    const side = Math.min(width, height);
    return { x: (width - side) / 2, y: (height - side) * (height > width ? TALL_CROP_FROM_TOP : 0.5), side };
};

const loadPicture = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const picture = new Image();
    picture.decoding = 'async';
    // A picture from another site: asked for with CORS, so the drawing can be read back (the cloud sends the header)
    if (isCloudPictureUrl(url)) picture.crossOrigin = 'anonymous';
    picture.onload = () => resolve(picture);
    picture.onerror = () => reject(new Error('The picture could not be loaded.'));
    picture.src = url;
});

/** The picture cut to the library's square, as a JPEG data URL; undefined when it cannot be drawn or comes out too large */
export const drawCover = (picture: CanvasImageSource & { naturalWidth: number; naturalHeight: number }, size: number = THUMBNAIL_SIZE): string | undefined => {
    if (!picture.naturalWidth || !picture.naturalHeight) return undefined;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const crop = coverCrop(picture.naturalWidth, picture.naturalHeight);
    ctx.drawImage(picture, crop.x, crop.y, crop.side, crop.side, 0, 0, size, size);
    const url = canvas.toDataURL('image/jpeg', COVER_QUALITY);
    return url.startsWith('data:image/') && url.length <= MAX_COVER_CHARS ? url : undefined;
};

/**
 * The workspace's cover, from its newest generated picture on this device; undefined when it has
 * none here, or when drawing takes longer than `waitMs`.
 */
export const workspaceCover = (nodes: ReadonlyArray<CanvasNode>, options: DisplayOptions, waitMs: number = COVER_WAIT_MS): Promise<string | undefined> => {
    const pageOrigin = window.location.origin;
    const work = (async () => {
        for (const ref of coverCandidates(nodes).slice(0, MOST_TRIED)) {
            try {
                const url = await drawableAddress(ref, options, pageOrigin);
                if (!url) continue;
                const cover = drawCover(await loadPicture(url));
                if (cover) return cover;
            } catch {
                /* the next one */
            }
        }
        return undefined;
    })();
    return withinTime(work, waitMs);
};
